import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Input, VStack, Select, Text, Heading, SimpleGrid, createListCollection } from "@chakra-ui/react";
import { profileTexts } from "../i18n/profile";
import { LanguageContext } from "../contexts/LanguageContext";
import { resumePendingReserve } from "../services/pendingReserve";
const API_BASE_URL = process.env.REACT_APP_API_URL;

const inputStyles = {
  bg: "white",
  borderColor: "gray.300",
  color: "gray.900",
  _placeholder: { color: "gray.400" },
  _focusVisible: { borderColor: "gray.900", boxShadow: "none" },
};

const ProfileForm = () => {
  const { language, setLanguage } = useContext(LanguageContext);
  const t = profileTexts[language] || profileTexts["et"];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: 'Estonia',
    province: '',
    municipality: '',
    settlement: '',
    street: '',
    house: '',
    apartment: '',
    postal_code: '',
    phone: '+372',
    language: language, // store 'et' or 'en' instead of 'Eesti'
    institution: '',
  });
  const [errors, setErrors] = useState({});
  const [autofillMsg, setAutofillMsg] = useState('');
  const navigate = useNavigate();

  const formatText = (text) => {
    if (!text || typeof text !== 'string') return '';
    const lowercaseWords = ['küla', 'linn', 'maakond', 'vald'];

    text = text
      .split(' ')
      .map(word => {
        let formattedWord = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        return lowercaseWords.includes(formattedWord.toLowerCase()) ? formattedWord.toLowerCase() : formattedWord;
      })
      .join(' ');

    text = text
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('-');

    return text;
  };

  const validateInput = (name, value, t) => {
    let error = "";
    // Name + address are required before renting is allowed
    if (["firstName", "lastName", "country", "province", "municipality",
         "settlement", "street", "house"].includes(name)) {
      if (!String(value || '').trim()) error = t.errors.required;
    }
    if (name === "email") {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(value)) error = t.errors.invalidEmail;
    }
    if (name === "phone") {
      const phonePattern = /^\+?\d{10,15}$/;
      if (!phonePattern.test(value)) error = t.errors.invalidPhone;
    }
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const formattedValue = ["firstName", "lastName"].includes(name) ? formatText(value) : value;

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue,
    }));

    // update language context if user changes language
    if (name === "language") setLanguage(formattedValue);

    const error = validateInput(name, formattedValue, t);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const getCSRFToken = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/csrf/`, { credentials: 'include' });
      const data = await res.json();
      return data.csrfToken;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // Consent = clicking the button: copy linked shop profile data into the form
  const handleAutofill = async () => {
    setAutofillMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/autofill/`, {
        credentials: 'include',
      });
      if (res.status === 404) {
        setAutofillMsg(t.autofillNone);
        return;
      }
      if (!res.ok) throw new Error(`Autofill failed: ${res.status}`);
      const data = await res.json();
      setFormData(prev => ({ ...prev, ...data }));
      setAutofillMsg(t.autofillDone);
    } catch (err) {
      console.error(err);
      setAutofillMsg(t.autofillError);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateInput(key, formData[key], t);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const formattedData = {
      ...formData,
      firstName: formatText(formData.firstName),
      lastName: formatText(formData.lastName),
      country: formatText(formData.country || ''),
      province: formatText(formData.province || ''),
      municipality: formatText(formData.municipality || ''),
      settlement: formatText(formData.settlement || ''),
      street: formatText(formData.street || ''),
      house: String(formData.house || ''),
      apartment: String(formData.apartment || ''),
      postal_code: String(formData.postal_code || ''),
      institution: String(formData.institution || ''),
    };

    try {
      const csrfToken = await getCSRFToken();
      const response = await fetch(`${API_BASE_URL}/api/profile/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify(formattedData),
        credentials: 'include',
      });

      if (response.ok) {
        // Profile is now complete — continue with a remembered instrument
        const resumed = await resumePendingReserve(navigate);
        if (!resumed) navigate('/');
      } else {
        const errorData = await response.json();
        setErrors(errorData || {});
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      const csrfToken = await getCSRFToken();
      try {
        const res = await fetch(`${API_BASE_URL}/api/profile/`, {
          method: 'GET',
          headers: { 'X-CSRFToken': csrfToken },
          credentials: 'include',
        });

        if (res.ok) {
          const data = await res.json();
          setFormData(prev => ({
            ...prev,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            country: data.country || 'Estonia',
            province: data.province || '',
            municipality: data.municipality || '',
            settlement: data.settlement || '',
            street: data.street || '',
            house: data.house || '',
            apartment: data.apartment || '',
            postal_code: data.postal_code || '',
            phone: data.phone || '+372',
            language: data.language || 'Estonian',
            institution: data.institution || '',
          }));
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchProfile();
  }, []);

  const languageCollection = createListCollection({
    items: [
      { label: "Eesti", value: "Eesti" },
      { label: "English", value: "English" },
    ],
  });

  return (
    <Box minH="calc(100vh - 4rem)" bg="gray.50" py={{ base: 8, md: 12 }} px={4}>
      <Box
        maxW="600px"
        mx="auto"
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <Box textAlign="center" mb={6}>
          <Text
            fontFamily="space"
            fontSize="xs"
            letterSpacing="0.3em"
            textTransform="uppercase"
            color="gray.500"
            mb={2}
          >
            akordion.ee
          </Text>
          <Heading fontFamily="heading" fontWeight="500" fontSize="3xl" color="gray.900">
            {t.title}
          </Heading>
        </Box>

        <Button
          onClick={handleAutofill}
          variant="outline"
          size="sm"
          w="full"
          mb={2}
          borderColor="gray.300"
          color="gray.700"
          borderRadius="sm"
          fontFamily="space"
          fontSize="xs"
          letterSpacing="0.12em"
          textTransform="uppercase"
          _hover={{ bg: "gray.100" }}
        >
          {t.autofill}
        </Button>
        {autofillMsg && (
          <Text fontSize="sm" color="gray.600" textAlign="center" mb={2}>
            {autofillMsg}
          </Text>
        )}

        <form onSubmit={handleSubmit}>
          <VStack gap={4}>
            <SimpleGrid columns={2} gap={4} w="full">
              <Input {...inputStyles} placeholder={t.firstName} name="firstName" value={formData.firstName} onChange={handleInputChange} />
              <Input {...inputStyles} placeholder={t.lastName} name="lastName" value={formData.lastName} onChange={handleInputChange} />
            </SimpleGrid>

            <SimpleGrid columns={2} gap={4} w="full">
              <Input {...inputStyles} placeholder={t.country} name="country" value={formData.country} onChange={handleInputChange} />
              <Input {...inputStyles} placeholder={t.province} name="province" value={formData.province} onChange={handleInputChange} />
            </SimpleGrid>

            <Input {...inputStyles} placeholder={t.municipality} name="municipality" value={formData.municipality} onChange={handleInputChange} />
            <Input {...inputStyles} placeholder={t.settlement} name="settlement" value={formData.settlement} onChange={handleInputChange} />
            <Input {...inputStyles} placeholder={t.street} name="street" value={formData.street} onChange={handleInputChange} />

            <SimpleGrid columns={3} gap={4} w="full">
              <Input {...inputStyles} placeholder={t.house} name="house" value={formData.house} onChange={handleInputChange} />
              <Input {...inputStyles} placeholder={t.apartment} name="apartment" value={formData.apartment} onChange={handleInputChange} />
              <Input {...inputStyles} placeholder={t.postalCode} name="postal_code" value={formData.postal_code} onChange={handleInputChange} />
            </SimpleGrid>

            <Input {...inputStyles} placeholder={t.phone} name="phone" value={formData.phone} onChange={handleInputChange} />
            {errors.phone && <Text color="red.500" fontSize="sm">{errors.phone}</Text>}

            <Input {...inputStyles} placeholder={t.email} name="email" value={formData.email} onChange={handleInputChange} />
            {errors.email && <Text color="red.500" fontSize="sm">{errors.email}</Text>}

            <Input {...inputStyles} placeholder={t.institution} name="institution" value={formData.institution} onChange={handleInputChange} />

            <Select.Root
              collection={languageCollection}
              value={[formData.language]}
              onValueChange={(details) => {
                setFormData((prev) => ({
                  ...prev,
                  language: details.value[0],
                }));
              }}
              required
            >
              <Select.HiddenSelect />

              <Select.Control bg="white">
                <Select.Trigger {...inputStyles}>
                  <Select.ValueText placeholder={t.languagePlaceholder} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>

              <Select.Positioner>
                <Select.Content>
                  {languageCollection.items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Select.Root>

            <Button
              type="submit"
              w="full"
              size="lg"
              bg="#09090b"
              color="white"
              borderRadius="sm"
              fontFamily="space"
              fontSize="xs"
              fontWeight="500"
              letterSpacing="0.14em"
              textTransform="uppercase"
              _hover={{ bg: "gray.800" }}
            >
              {t.save}
            </Button>

            {errors && Object.keys(errors).filter(key => errors[key] && !["phone", "email"].includes(key)).map(key => (
              <Text key={key} color="red.500" fontSize="sm">
                {t[key] ? `${t[key]}: ` : ""}{errors[key]}
              </Text>
            ))}
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default ProfileForm;
